import {useLayoutEffect} from "react";
import {AUTHENTICATION_MODEL, STEPS_CREATE_DEVICE} from "../Model/Model";
import {errorMsg} from "../../../../Apis/functions";
import {
    CreateDepartment,
    CreateDeviceApi,
    CreateEmployee
} from "../../../../reduxToolkit/Devices/DevicesApi";
import {toast} from "react-toastify";
import routes from "../../../../Routes/routes";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 * Created At: 04 de 05 de 2026
 */

/**
 * Custom hook used to handle device creation workflow.
 *
 * This hook manages:
 * - Step initialization.
 * - Device creation request.
 * - Employee and department assignments.
 * - 1FA and 2FA access configuration.
 * - Navigation after successful creation.
 * - API error handling.
 *
 * @function CreateDevicesApp
 *
 * @param {Object} props - Hook parameters.
 * @param {Function} props.setSteps - Updates device creation step list.
 * @param {Function} props.handleError - Global API error handler.
 * @param {Array<Object>} props.deviceCustomAccess - Employees assigned to 1FA access.
 * @param {Function} props.setDeviceCustomAccess - Updates 1FA employee assignments.
 * @param {Array<Object>} props.deviceDepartment - Departments assigned to 1FA access.
 * @param {Function} props.setDeviceDepartment - Updates 1FA department assignments.
 * @param {Array<Object>} props.deviceCustomAccessTwoFa - Employees assigned to 2FA access.
 * @param {Function} props.setDeviceCustomAccessTwoFa - Updates 2FA employee assignments.
 * @param {Array<Object>} props.deviceDepartmentTwoFa - Departments assigned to 2FA access.
 * @param {Function} props.setDeviceDepartmentTwoFa - Updates 2FA department assignments.
 * @param {string} props.zoneNameProps - Current zone name.
 * @param {number|string} props.zoneIdProps - Current zone identifier.
 * @param {Function} props.setModified - Updates modified state.
 * @param {Array<number>} props.defaultDistance - Default distance configuration.
 * @param {Object} props.data - Device form data model.
 * @param {Function} props.t - Translation function.
 * @param {Function} props.dispatch - Redux dispatch function.
 * @param {Function} props.navigate - Navigation handler.
 * @param {Function} props.setEmptyFields - Updates validation state.
 * @param {number|string|undefined} props.id - Device identifier.
 *
 * @returns {Object} Hook response object.
 * @returns {Function} returns.handleCreate - Executes device creation flow.
 */
export const CreateDevicesApp = ({
                                     setSteps,
                                     handleError,
                                     deviceCustomAccess,
                                     setDeviceCustomAccess,
                                     deviceDepartment,
                                     setDeviceDepartment,
                                     deviceCustomAccessTwoFa,
                                     setDeviceCustomAccessTwoFa,
                                     deviceDepartmentTwoFa,
                                     setDeviceDepartmentTwoFa,
                                     zoneNameProps,
                                     zoneIdProps,
                                     setModified,
                                     defaultDistance,
                                     data,
                                     t,
                                     dispatch,
                                     navigate,
                                     setEmptyFields,
                                     id
                                 }) => {

    useLayoutEffect(() => {
        const stepArray = Object.values(STEPS_CREATE_DEVICE)
            .filter(step => step?.TRANS && typeof step.INDEX === "number")
            .sort((a, b) => a.INDEX - b.INDEX)
            .map(step => t(step.TRANS))
        setSteps(stepArray)
    }, [t])

    /**
     * Main device creation handler.
     *
     * Handles:
     * - Device creation.
     * - Employee relationship creation.
     * - Department relationship creation.
     * - 1FA and 2FA configuration.
     * - Navigation after success.
     */
    const handleCreate = async () => {
        const {
            data: d,
            options: o,
            security: s,
            authentication: a,
            relay: r,
            unoFa,
            dosFa
        } = data;

        const body = {
            name: d.deviceName,
            deviceId: d.idDevice,
            ip: d.ip,
            mac: d.mac,
            serialNumber: d.sn,
            description: d.description,
            deviceAccessTypeId: d.accessType,
            deviceTypeId: d.deviceType,
            statusId: d.statusValue,
            zoneId: d.zoneName === '' ? null : d.zoneName,
            password:
                d.password &&
                typeof d.password === 'string' &&
                d.password.trim() !== ''
                    ? d.password
                    : null,
            modeId: d.modeId,

            antiPassBackId: s.antiPassBackType,
            antiPassBackTime: s.antiPassBackTime,
            antiPassBackAlertById: s.antiPassBackAlert,
            escortId: s.escortMode,
            validationNumber: s.validationNumber,
            syncTime: s.syncTime,
            realTimeSync: s.realTimeSync ?? false,
            syncAtTime: s.syncAtTime ?? null,
            requestSignature: s.requestSignature,
            requestSignatureAlways: s.requestSignatureAlways,

            authenticationMethod: a.authentication,
            bluetoothReader: a.bluetooth,
            rfidReader: a.smartCard,
            facialRecognition: a.facialRecognition,
            liveness: a.liveness,
            fingerprintReader: a.fingerprint,
            pin: a.pin,
            qrCodeReader: a.qr,
            firebaseId: null,
            readAfter: a.readAfter,
            cameraTypeId: a.cameraTypeId,
            enableDistance: a.enableDistance,
            distance:
                a.enableDistance === true
                    ? a.distance
                    : defaultDistance[d.deviceType],
            consecutiveOpeningsAllowed: a.consecutiveOpeningsAllowed,


            timeZoneId: o.timeZoneValue,
            synchronizeSelfie: o.synchorizeSelfies,
            playSound: o.playSounds,
            showLogo: o.showLogo,
            showUserList: o.showUserList,
            showWeapon: o.showWeapon,
            showVehicle: o.showVehicle,
            showLed: o.showLed,
            showAccessDialog: o.showAccessDialog,
            showLastAccesses: o.showLastAccess,
            printer: o.printer,


            delayToOpen: r.delayToOpen,
            keepOpenRelay: r.keepOpenRelay,

            enableRelay1FA: unoFa.enableRelay1FA,
            openRelayForAll1FA: unoFa.openRelayForAll1FA,


            enableRelay2FA: dosFa.enableRelay2FA,
            openRelayForAll2FA: dosFa.openRelayForAll2FA
        };


        /**
         * Processes employee or department access assignments.
         *
         * @async
         *
         * @param {Object} config - Access configuration.
         * @param {Array<Object>} config.list - Items to process.
         * @param {Function} config.getResourceId - Extracts resource identifier.
         * @param {number} config.authenticationMode - Authentication mode identifier.
         * @param {Function} config.action - Redux action dispatcher.
         * @param {number|string} config.deviceId - Device identifier.
         * @param {Function} config.onSuccessClear - Clears local state after success.
         */
        const processAccess = async ({
                                         list,
                                         getResourceId,
                                         authenticationMode,
                                         action,
                                         deviceId,
                                         onSuccessClear
                                     }) => {
            if (!list || list.length === 0) return;

            const validList = list.filter(e => {
                const id = getResourceId(e);
                return id !== null && id !== undefined;
            });

            if (validList.length === 0) return;

            const payload = validList.map(e => ({
                deviceId,
                resourceId: getResourceId(e),
                authenticationMode
            }));

            const {payload: {data: {code, success}}} = await dispatch(action(payload));

            if (success) {
                setModified(true);
                onSuccessClear();
            } else {
                errorMsg(code);
            }
        };

        setEmptyFields(false);

        try {
            let response;
            response = await dispatch(CreateDeviceApi(body));

            const {success, code, data: responseData} = response.payload.data;

            if (!success) {
                errorMsg(code);
                return;
            }

            const deviceIdFinal = responseData?.id;

            await processAccess({
                list: deviceCustomAccess,
                getResourceId: e => e.userId,
                authenticationMode: AUTHENTICATION_MODEL.ONE_FA,
                action: CreateEmployee,
                deviceId: deviceIdFinal,
                onSuccessClear: () => setDeviceCustomAccess([])
            });

            await processAccess({
                list: deviceDepartment,
                getResourceId: e => e.departmentId,
                authenticationMode: AUTHENTICATION_MODEL.ONE_FA,
                action: CreateDepartment,
                deviceId: deviceIdFinal,
                onSuccessClear: () => setDeviceDepartment([])
            });

            await processAccess({
                list: deviceCustomAccessTwoFa,
                getResourceId: e => e.userId,
                authenticationMode: AUTHENTICATION_MODEL.TWO_FA,
                action: CreateEmployee,
                deviceId: deviceIdFinal,
                onSuccessClear: () => setDeviceCustomAccessTwoFa([])
            });

            await processAccess({
                list: deviceDepartmentTwoFa,
                getResourceId: e => e.departmentId,
                authenticationMode: AUTHENTICATION_MODEL.TWO_FA,
                action: CreateDepartment,
                deviceId: deviceIdFinal,
                onSuccessClear: () => setDeviceDepartmentTwoFa([])
            });

            toast.success(t('has_been_created_successfully').toUpperCase());

            navigate(
                zoneNameProps !== undefined
                    ? routes.zone.details + zoneIdProps
                    : id
                        ? setModified(true)
                        : routes.devices.list
            );

        } catch (error) {
            handleError(error);
        }
    };


    return {
        handleCreate
    }

}
